import { DAY_MS, jstDateOnly, jstParts } from '@shared/time.ts'
import { type Component, createEffect, createResource, createSignal, on, Show } from 'solid-js'
import { ProgramList } from '~/components/ProgramList'
import { fetchSchedule } from '~/lib/api'
import { WEEKDAY_LABELS } from '~/lib/labels'

type Props = {
  /** サーバー時刻基準の現在時刻（ミリ秒） */
  nowMs: number
  currentStartAt?: string
  /**
   * 番組表が出し直されたことを知るための値。変わるとスクロールし直す。
   * 狭い画面ではタブ、広い画面では出しっぱなしなので、
   * 「見えているか」は高さで判断する（clientHeight が 0 なら測らない）。
   */
  reveal?: unknown
}

/**
 * 何日先まで見られるか。
 *
 * 翌週ぶんの番組表は日曜の未明に作られるので、確定しているのは長くても今週いっぱい。
 * それより先も直近の編成を当てはめて返るが、確定ではないことを画面で断ったうえで、
 * 「今週の残り」に届く 1 週間ぶんまでにしておく。
 */
const MAX_DAYS_AHEAD = 6

/** 日付の表示。例: 8/30(日) */
export const guideDateLabel = (epochMs: number): string => {
  const { month, day, weekday } = jstParts(epochMs)
  return `${month}/${day}(${WEEKDAY_LABELS[weekday]})`
}

/** 今日・明日だけは日付より分かりやすいので言葉で添える */
export const relativeDayLabel = (offset: number): string | undefined => {
  if (offset === 0) return '今日'
  if (offset === 1) return '明日'
  return undefined
}

/**
 * PROGRAM: 1 日ぶんの番組表（要件定義 17 章）。
 *
 * 今日から 1 週間ぶんを日付で送り戻しできる。
 * 1 日ぶんは 900 番組を超えるので、今日を開いたときは放送中の番組が先頭に来るまでスクロールする。
 */
export const ProgramGuide: Component<Props> = (props) => {
  /** 今日からの日数。0 が今日 */
  const [offset, setOffset] = createSignal(0)

  const dayMs = (): number => props.nowMs + offset() * DAY_MS
  // 日付が変わったときだけ取り直す
  const date = () => (props.nowMs > 0 ? jstDateOnly(dayMs()) : undefined)
  const [schedule] = createResource(date, (day) => fetchSchedule(day))

  const move = (days: number): void => {
    setOffset((current) => Math.min(MAX_DAYS_AHEAD, Math.max(0, current + days)))
  }

  let scroller!: HTMLDivElement

  /** 放送中の番組が先頭に来るまでスクロールする */
  const scrollToCurrent = (): void => {
    // 非表示のあいだは高さが取れないので測らない
    if (scroller.clientHeight === 0) return
    const target = scroller.querySelector<HTMLElement>('[data-current="true"]')
    if (!target) return
    scroller.scrollTop = scrollTopForCurrent({
      scrollTop: scroller.scrollTop,
      scrollerTop: scroller.getBoundingClientRect().top,
      targetTop: target.getBoundingClientRect().top,
    })
  }

  // currentStartAt そのものは番組が変わるたびに動くので依存には入れない。
  // 読んでいる途中で勝手にスクロールが戻らないよう、放送中かどうかの変化だけを見る
  createEffect(
    on(
      [() => props.reveal, () => schedule.state, () => props.currentStartAt !== undefined, offset],
      () => {
        // 一覧が並んでから位置を測る。隠れている間は高さが 0 なので何もしない
        requestAnimationFrame(() => {
          // 先の日付には放送中の番組が無いので、頭から読ませる
          if (offset() === 0) scrollToCurrent()
          else scroller.scrollTop = 0
        })
      },
    ),
  )

  const arrow =
    'shrink-0 select-none rounded-ch px-2 py-1 text-ch-muted transition-colors hover:bg-ch-elevated hover:text-ch-text disabled:pointer-events-none disabled:opacity-30'

  return (
    <div class="flex min-h-0 flex-1 flex-col">
      <div class="mb-2 flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => move(-1)}
          disabled={offset() === 0}
          aria-label="前の日の番組表"
          class={arrow}
        >
          ‹
        </button>

        <p class="flex min-w-0 flex-1 items-baseline justify-center gap-1.5">
          <span class="font-mono text-sm tabular">{guideDateLabel(dayMs())}</span>
          <Show when={relativeDayLabel(offset())}>
            {(label) => <span class="text-[11px] font-semibold text-ch-accent">{label()}</span>}
          </Show>
        </p>

        <button
          type="button"
          onClick={() => move(1)}
          disabled={offset() === MAX_DAYS_AHEAD}
          aria-label="次の日の番組表"
          class={arrow}
        >
          ›
        </button>
      </div>

      {/* まだ組まれていない日は、確定した編成ではないことを断る */}
      <Show when={schedule()?.rerun}>
        <p class="mb-2 shrink-0 rounded-ch bg-ch-elevated px-2.5 py-1.5 text-[11px] leading-relaxed text-ch-muted">
          この日の番組表はまだ組まれていません。いまの編成をそのまま当てはめた予定です。
        </p>
      </Show>

      <div ref={scroller} class="min-h-0 flex-1 overflow-y-auto">
        <Show
          when={!schedule.error}
          fallback={<ProgramList programs={[]} emptyText="番組表を取得できませんでした" />}
        >
          <ProgramList
            programs={schedule()?.programs ?? []}
            currentStartAt={offset() === 0 ? props.currentStartAt : undefined}
            emptyText={schedule.loading ? '読み込み中…' : 'この日の番組はありません'}
          />
        </Show>
      </div>
    </div>
  )
}

/** 放送中の番組が先頭に来るスクロール位置 */
export const scrollTopForCurrent = (measure: {
  scrollTop: number
  scrollerTop: number
  targetTop: number
}): number => Math.max(0, measure.scrollTop + (measure.targetTop - measure.scrollerTop))
